<template>
  <div class="panel mt-4">
    <div class="w-full flex items-center justify-between mb-4">
      <div class="uppercase text-blue-light font-thin text-lg">
        {{ $t('materials') }}
      </div>
      <!-- Expande el panel con las 11 columnas (deja el sidebar visible). -->
      <b-button
        v-if="rows.length"
        size="is-small"
        type="is-info"
        outlined
        icon-pack="fas"
        icon-left="expand-arrows-alt"
        @click="isFullTableOpen = true"
      >
        {{ $t('expand') }}
      </b-button>
    </div>

    <b-loading :active="isLoading" :is-full-page="false" />

    <!-- Vista compacta: 5 columnas clave, sin scroll horizontal. -->
    <b-table
      :data="rows"
      striped
      bordered
      narrowed
      mobile-cards
      icon-pack="fas"
      paginated
      backend-pagination
      :total="total"
      :per-page="perPage"
      @page-change="onPageChange"
      class="w-full"
    >
      <b-table-column field="unique_material_id" :label="$t('unique_material_id')" width="160" v-slot="props">
        {{ props.row.unique_material_id || '-' }}
      </b-table-column>
      <b-table-column field="description" :label="$t('description')" v-slot="props">
        {{ props.row.description || '-' }}
      </b-table-column>
      <b-table-column field="provider" :label="$t('provider')" width="180" v-slot="props">
        {{ props.row.provider || '-' }}
      </b-table-column>
      <b-table-column field="unit" :label="$t('unit')" width="100" v-slot="props">
        {{ props.row.unit || '-' }}
      </b-table-column>
      <b-table-column field="quantity" :label="$t('stock')" numeric width="110" v-slot="props">
        {{ props.row.quantity != null ? props.row.quantity : '-' }}
      </b-table-column>

      <template #empty>
        <div class="has-text-centered">{{ $t('no_results') }}</div>
      </template>
    </b-table>

    <!-- Overlay propio: respeta el sidebar en lg+ (ocupa 5/6 derechos). En mobile, cubre todo. -->
    <div
      v-if="isFullTableOpen"
      class="fixed inset-y-0 right-0 w-full lg:w-5/6 bg-black bg-opacity-40 flex flex-col p-4"
      style="z-index: 60;"
    >
      <div class="flex-1 bg-white rounded-lg shadow-2xl flex flex-col overflow-hidden">
        <div class="flex items-center justify-between p-4 border-b border-gray-200">
          <div class="uppercase text-blue-light font-thin text-lg">
            {{ $t('materials') }}
          </div>
          <button type="button" class="delete" :aria-label="$t('close')" @click="isFullTableOpen = false" />
        </div>
        <div class="flex-1 overflow-auto p-4">
          <b-table
            :data="rows"
            striped
            bordered
            narrowed
            icon-pack="fas"
            paginated
            backend-pagination
            :total="total"
            :per-page="perPage"
            @page-change="onPageChange"
            class="w-full"
          >
            <b-table-column field="unique_material_id" :label="$t('unique_material_id')" v-slot="props">
              {{ props.row.unique_material_id || '-' }}
            </b-table-column>
            <b-table-column field="description" :label="$t('description')" v-slot="props">
              {{ props.row.description || '-' }}
            </b-table-column>
            <b-table-column field="provider" :label="$t('provider')" v-slot="props">
              {{ props.row.provider || '-' }}
            </b-table-column>
            <b-table-column field="unit" :label="$t('unit')" v-slot="props">
              {{ props.row.unit || '-' }}
            </b-table-column>
            <b-table-column field="quantity" :label="$t('stock')" numeric v-slot="props">
              {{ props.row.quantity != null ? props.row.quantity : '-' }}
            </b-table-column>
            <b-table-column field="drawing_number" :label="$t('drawing_number')" v-slot="props">
              {{ props.row.drawing_number || '-' }}
            </b-table-column>
            <b-table-column field="tag_kks" :label="$t('tag_kks')" v-slot="props">
              {{ props.row.tag_kks || '-' }}
            </b-table-column>
            <b-table-column field="storehouse" :label="$t('storehouse')" v-slot="props">
              {{ props.row.storehouse || '-' }}
            </b-table-column>
            <b-table-column field="location" :label="$t('location')" v-slot="props">
              {{ props.row.location || '-' }}
            </b-table-column>
            <b-table-column field="status" :label="$t('status')" v-slot="props">
              {{ props.row.status || '-' }}
            </b-table-column>
            <b-table-column field="comments" :label="$t('comments')" v-slot="props">
              {{ props.row.comments || '-' }}
            </b-table-column>

            <template #empty>
              <div class="has-text-centered">{{ $t('no_results') }}</div>
            </template>
          </b-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'activity-warehouse-inventory',
  props: {
    projectId: { type: [Number, String], required: true },
    activityId: { type: [Number, String], required: true },
  },
  data: () => ({
    isLoading: false,
    rows: [],
    page: 1,
    perPage: 10,              // card compacto: espacio reducido, menos filas visibles
    total: 0,
    isFullTableOpen: false,
    escListener: null,
  }),
  computed: {
    normalizedProjectId() {
      return Number(this.projectId) || null
    },
    normalizedActivityId() {
      return Number(this.activityId) || null
    },
  },
  watch: {
    normalizedProjectId() {
      this.load()
    },
    normalizedActivityId() {
      this.load()
    },
    isFullTableOpen(open) {
      // Card compacto = 10; tabla completa aprovecha espacio = 25.
      this.perPage = open ? 25 : 10
      this.page = 1
      this.load()
      if (open) this.attachEscListener()
      else this.detachEscListener()
    },
  },
  mounted() {
    this.load()
  },
  beforeUnmount() {
    this.detachEscListener()
  },
  methods: {
    attachEscListener() {
      this.escListener = (e) => {
        if (e.key === 'Escape') this.isFullTableOpen = false
      }
      window.addEventListener('keydown', this.escListener)
    },
    detachEscListener() {
      if (this.escListener) {
        window.removeEventListener('keydown', this.escListener)
        this.escListener = null
      }
    },
    onPageChange(page) {
      const p = Number(page)
      this.page = Number.isFinite(p) && p > 0 ? p : 1
      this.load()
    },
    async load() {
      if (!this.normalizedProjectId || !this.normalizedActivityId) {
        this.rows = []
        this.total = 0
        return
      }
      this.isLoading = true
      try {
        const { data } = await axios.get(`/api/v2/project/${this.normalizedProjectId}/warehouse/inventory`, {
          params: {
            per_page: this.perPage,
            page: this.page,
            order_field: 'id',
            order_dir: 'desc',
            with_bulk: 0,
            material_activity_id: this.normalizedActivityId,
          },
        })
        this.rows = (data && data.success && Array.isArray(data.inventory)) ? data.inventory : []
        this.total = (data && typeof data.total === 'number') ? data.total : Number(data && data.total) || 0
      } catch (e) {
        this.rows = []
        this.total = 0
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>
